import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../hooks/useTranslation';
import { useCurrency } from '../hooks/useCurrency';

interface Package {
    id: string;
    name: string;
    destination: string;
    country: string;
    description: string;
    imageUrl: string;
    duration: string;
    price: number;
    originalPrice?: number;
    rating: number;
    category: 'budget' | 'standard' | 'premium' | 'luxury';
    includes: string[];
    highlights: string[];
    itinerary: {
        day: number;
        title: string;
        description: string;
        activities: string[];
    }[];
    isPopular: boolean;
    isFeatured: boolean;
    discount?: number;
    groupSize: string;
    difficulty: 'easy' | 'moderate' | 'challenging';
}

const Packages: React.FC = () => {
    const { t } = useTranslation();
    const { formatPrice } = useCurrency();
    const [packages, setPackages] = useState<Package[]>([]);
    const [filteredPackages, setFilteredPackages] = useState<Package[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    
    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedDuration, setSelectedDuration] = useState('');
    const [priceRange, setPriceRange] = useState([0, 10000]);
    const [selectedRating, setSelectedRating] = useState(0);
    const [sortBy, setSortBy] = useState('name');

    // Sample packages data
    const mockPackages: Package[] = [
        {
            id: '1',
            name: 'Istanbul Cultural Discovery',
            destination: 'Istanbul',
            country: 'Turkey',
            description: 'Immerse yourself in the rich history and culture of Istanbul with guided tours of iconic landmarks.',
            imageUrl: '/hotell.jpg.png',
            duration: '5 days / 4 nights',
            price: 899,
            originalPrice: 1199,
            rating: 5,
            category: 'premium',
            includes: ['Hotel accommodation', 'Daily breakfast', 'Airport transfers', 'Professional guide', 'Entrance fees'],
            highlights: ['Hagia Sophia', 'Blue Mosque', 'Topkapi Palace', 'Bosphorus Cruise', 'Grand Bazaar'],
            itinerary: [
                {
                    day: 1,
                    title: 'Arrival & Welcome',
                    description: 'Arrive in Istanbul and transfer to your hotel. Evening welcome dinner.',
                    activities: ['Airport pickup', 'Hotel check-in', 'Welcome dinner']
                },
                {
                    day: 2,
                    title: 'Historic Peninsula',
                    description: 'Explore the heart of historic Istanbul with guided tours.',
                    activities: ['Hagia Sophia', 'Blue Mosque', 'Hippodrome', 'Basilica Cistern']
                },
                {
                    day: 3,
                    title: 'Ottoman Splendor',
                    description: 'Discover the opulence of the Ottoman Empire.',
                    activities: ['Topkapi Palace', 'Harem section', 'Imperial Treasury']
                },
                {
                    day: 4,
                    title: 'Bosphorus & Markets',
                    description: 'Cruise the Bosphorus and explore traditional markets.',
                    activities: ['Bosphorus cruise', 'Grand Bazaar', 'Spice Bazaar']
                },
                {
                    day: 5,
                    title: 'Departure',
                    description: 'Last-minute shopping and transfer to airport.',
                    activities: ['Free time', 'Airport transfer']
                }
            ],
            isPopular: true,
            isFeatured: true,
            discount: 25,
            groupSize: 'Max 15 people',
            difficulty: 'easy'
        },
        {
            id: '2',
            name: 'Dubai Luxury Escape',
            destination: 'Dubai',
            country: 'UAE',
            description: 'Experience the ultimate luxury in Dubai with 5-star accommodations and exclusive experiences.',
            imageUrl: '/hote12.jpg.png',
            duration: '6 days / 5 nights',
            price: 2499,
            originalPrice: 2999,
            rating: 5,
            category: 'luxury',
            includes: ['5-star hotel', 'All meals', 'Private transfers', 'Personal concierge', 'Exclusive experiences'],
            highlights: ['Burj Khalifa', 'Palm Jumeirah', 'Desert Safari', 'Luxury shopping', 'Fine dining'],
            itinerary: [
                {
                    day: 1,
                    title: 'Luxury Arrival',
                    description: 'Arrive in style with VIP airport service and luxury hotel check-in.',
                    activities: ['VIP airport service', 'Luxury hotel check-in', 'Welcome cocktail']
                },
                {
                    day: 2,
                    title: 'Iconic Dubai',
                    description: 'Visit Dubai\'s most iconic landmarks and attractions.',
                    activities: ['Burj Khalifa', 'Dubai Mall', 'Dubai Fountain']
                },
                {
                    day: 3,
                    title: 'Desert Adventure',
                    description: 'Experience the magic of the Arabian Desert.',
                    activities: ['Desert safari', 'Camel riding', 'Traditional dinner']
                },
                {
                    day: 4,
                    title: 'Modern Wonders',
                    description: 'Explore Dubai\'s modern architectural marvels.',
                    activities: ['Palm Jumeirah', 'Atlantis', 'Marina Walk']
                },
                {
                    day: 5,
                    title: 'Shopping & Culture',
                    description: 'Discover traditional markets and cultural experiences.',
                    activities: ['Gold Souk', 'Spice Souk', 'Cultural show']
                },
                {
                    day: 6,
                    title: 'Departure',
                    description: 'Last luxury experiences before departure.',
                    activities: ['Spa treatment', 'Luxury airport transfer']
                }
            ],
            isPopular: true,
            isFeatured: true,
            discount: 17,
            groupSize: 'Max 8 people',
            difficulty: 'easy'
        },
        {
            id: '3',
            name: 'Paris Romantic Getaway',
            destination: 'Paris',
            country: 'France',
            description: 'Perfect for couples seeking a romantic escape in the City of Light.',
            imageUrl: '/hote13.jpg.png',
            duration: '4 days / 3 nights',
            price: 1299,
            rating: 5,
            category: 'premium',
            includes: ['Boutique hotel', 'Daily breakfast', 'Metro passes', 'Guided tours', 'Romantic dinner'],
            highlights: ['Eiffel Tower', 'Louvre Museum', 'Seine River cruise', 'Notre-Dame', 'Champs-Élysées'],
            itinerary: [
                {
                    day: 1,
                    title: 'Romantic Arrival',
                    description: 'Arrive in Paris and settle into your boutique hotel.',
                    activities: ['Hotel check-in', 'Evening stroll', 'Welcome dinner']
                },
                {
                    day: 2,
                    title: 'Iconic Paris',
                    description: 'Visit the most romantic spots in Paris.',
                    activities: ['Eiffel Tower', 'Seine River cruise', 'Luxembourg Gardens']
                },
                {
                    day: 3,
                    title: 'Art & Culture',
                    description: 'Immerse yourself in Parisian art and culture.',
                    activities: ['Louvre Museum', 'Notre-Dame', 'Latin Quarter']
                },
                {
                    day: 4,
                    title: 'Farewell Paris',
                    description: 'Last romantic moments in Paris.',
                    activities: ['Champs-Élysées', 'Arc de Triomphe', 'Departure']
                }
            ],
            isPopular: false,
            isFeatured: false,
            groupSize: 'Max 12 people',
            difficulty: 'easy'
        },
        {
            id: '4',
            name: 'Tokyo Adventure',
            destination: 'Tokyo',
            country: 'Japan',
            description: 'Explore the fascinating blend of traditional and modern Japan in vibrant Tokyo.',
            imageUrl: '/hotell.jpg.png',
            duration: '7 days / 6 nights',
            price: 1899,
            rating: 4,
            category: 'standard',
            includes: ['Hotel accommodation', 'Daily breakfast', 'JR Pass', 'Guided tours', 'Cultural experiences'],
            highlights: ['Shibuya Crossing', 'Senso-ji Temple', 'Tokyo Skytree', 'Tsukiji Market', 'Akihabara'],
            itinerary: [
                {
                    day: 1,
                    title: 'Welcome to Tokyo',
                    description: 'Arrive in Tokyo and experience the energy of this amazing city.',
                    activities: ['Airport transfer', 'Hotel check-in', 'Evening exploration']
                },
                {
                    day: 2,
                    title: 'Traditional Tokyo',
                    description: 'Discover the traditional side of Tokyo.',
                    activities: ['Senso-ji Temple', 'Asakusa district', 'Traditional lunch']
                },
                {
                    day: 3,
                    title: 'Modern Tokyo',
                    description: 'Experience the futuristic side of Tokyo.',
                    activities: ['Tokyo Skytree', 'Shibuya Crossing', 'Harajuku']
                },
                {
                    day: 4,
                    title: 'Cultural Tokyo',
                    description: 'Immerse yourself in Japanese culture.',
                    activities: ['Tsukiji Market', 'Imperial Palace', 'Tea ceremony']
                },
                {
                    day: 5,
                    title: 'Entertainment Tokyo',
                    description: 'Explore Tokyo\'s entertainment districts.',
                    activities: ['Akihabara', 'Ginza', 'Evening show']
                },
                {
                    day: 6,
                    title: 'Nature Tokyo',
                    description: 'Discover Tokyo\'s natural beauty.',
                    activities: ['Ueno Park', 'Shinjuku Gyoen', 'Evening cruise']
                },
                {
                    day: 7,
                    title: 'Farewell Tokyo',
                    description: 'Last moments in Tokyo before departure.',
                    activities: ['Last shopping', 'Airport transfer']
                }
            ],
            isPopular: false,
            isFeatured: false,
            groupSize: 'Max 20 people',
            difficulty: 'moderate'
        },
        {
            id: '5',
            name: 'Bangkok Budget Explorer',
            destination: 'Bangkok',
            country: 'Thailand',
            description: 'Affordable adventure in the vibrant capital of Thailand.',
            imageUrl: '/hote12.jpg.png',
            duration: '4 days / 3 nights',
            price: 399,
            rating: 4,
            category: 'budget',
            includes: ['Hostel accommodation', 'Daily breakfast', 'Public transport', 'Group tours', 'Local experiences'],
            highlights: ['Grand Palace', 'Wat Phra Kaew', 'Chatuchak Market', 'Chao Phraya River', 'Street food'],
            itinerary: [
                {
                    day: 1,
                    title: 'Arrival in Bangkok',
                    description: 'Arrive and settle into your hostel in the heart of Bangkok.',
                    activities: ['Hostel check-in', 'Orientation walk', 'Street food dinner']
                },
                {
                    day: 2,
                    title: 'Royal Bangkok',
                    description: 'Explore the royal and religious sites of Bangkok.',
                    activities: ['Grand Palace', 'Wat Phra Kaew', 'Wat Arun']
                },
                {
                    day: 3,
                    title: 'Local Bangkok',
                    description: 'Experience authentic local life in Bangkok.',
                    activities: ['Chatuchak Market', 'Chinatown', 'Local markets']
                },
                {
                    day: 4,
                    title: 'Departure',
                    description: 'Last exploration before leaving Bangkok.',
                    activities: ['Free time', 'Airport transfer']
                }
            ],
            isPopular: false,
            isFeatured: false,
            groupSize: 'Max 25 people',
            difficulty: 'easy'
        }
    ];

    const categories = ['All', 'Budget', 'Standard', 'Premium', 'Luxury'];
    const durations = ['All', '1-3 days', '4-6 days', '7+ days'];
    const difficulties = ['All', 'Easy', 'Moderate', 'Challenging'];

    useEffect(() => {
        // Simulate loading
        setTimeout(() => {
            setPackages(mockPackages);
            setFilteredPackages(mockPackages);
            setIsLoading(false);
        }, 1000);
    }, []);

    useEffect(() => {
        // Apply filters
        let filtered = packages;

        // Search term filter
        if (searchTerm) {
            filtered = filtered.filter(pkg =>
                pkg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                pkg.destination.toLowerCase().includes(searchTerm.toLowerCase()) ||
                pkg.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
                pkg.description.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Category filter
        if (selectedCategory && selectedCategory !== 'All') {
            filtered = filtered.filter(pkg => pkg.category === selectedCategory.toLowerCase());
        }

        // Duration filter
        if (selectedDuration && selectedDuration !== 'All') {
            filtered = filtered.filter(pkg => {
                const days = parseInt(pkg.duration.split(' ')[0]);
                switch (selectedDuration) {
                    case '1-3 days':
                        return days <= 3;
                    case '4-6 days':
                        return days >= 4 && days <= 6;
                    case '7+ days':
                        return days >= 7;
                    default:
                        return true;
                }
            });
        }

        // Price filter
        filtered = filtered.filter(pkg => pkg.price >= priceRange[0] && pkg.price <= priceRange[1]);

        // Rating filter
        if (selectedRating > 0) {
            filtered = filtered.filter(pkg => pkg.rating >= selectedRating);
        }

        // Sort
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'price':
                    return a.price - b.price;
                case 'rating':
                    return b.rating - a.rating;
                case 'duration':
                    return parseInt(a.duration.split(' ')[0]) - parseInt(b.duration.split(' ')[0]);
                case 'name':
                default:
                    return a.name.localeCompare(b.name);
            }
        });

        setFilteredPackages(filtered);
    }, [packages, searchTerm, selectedCategory, selectedDuration, priceRange, selectedRating, sortBy]);

    const clearFilters = () => {
        setSearchTerm('');
        setSelectedCategory('All');
        setSelectedDuration('All');
        setPriceRange([0, 10000]);
        setSelectedRating(0);
        setSortBy('name');
    };

    const getCategoryColor = (category: string) => {
        switch (category) {
            case 'budget': return '#10b981';
            case 'standard': return '#3b82f6';
            case 'premium': return '#8b5cf6';
            case 'luxury': return '#f59e0b';
            default: return '#6b7280';
        }
    };

    const getDifficultyColor = (difficulty: string) => {
        switch (difficulty) {
            case 'easy': return '#10b981';
            case 'moderate': return '#f59e0b';
            case 'challenging': return '#ef4444';
            default: return '#6b7280';
        }
    };

    if (isLoading) {
        return (
            <div className="packages-page">
                <div className="container">
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p>{t('common.loading')}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="packages-page">
            <div className="container">
                {/* Hero Section */}
                <motion.section 
                    className="packages-hero"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <h1>{t('packages.title', 'Travel Packages')}</h1>
                    <p>{t('packages.subtitle', 'Discover amazing travel packages tailored for every budget and preference')}</p>
                </motion.section>

                {/* Featured Packages */}
                <motion.section 
                    className="featured-packages"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    <div className="section-header">
                        <h2>{t('packages.featured', 'Featured Packages')}</h2>
                        <p>{t('packages.featuredDesc', 'Our most popular and highly-rated travel experiences')}</p>
                    </div>
                    
                    <div className="featured-grid">
                        {packages.filter(p => p.isFeatured).map((pkg, index) => (
                            <motion.div
                                key={pkg.id}
                                className="featured-card"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: index * 0.1 }}
                                whileHover={{ y: -5, scale: 1.02 }}
                            >
                                <div className="featured-image">
                                    <img src={pkg.imageUrl} alt={pkg.name} />
                                    <div className="featured-badge">⭐ Featured</div>
                                    {pkg.discount && (
                                        <div className="discount-badge">-{pkg.discount}%</div>
                                    )}
                                    <div className="featured-rating">
                                        {'⭐'.repeat(pkg.rating)}
                                    </div>
                                </div>
                                <div className="featured-content">
                                    <div className="package-meta">
                                        <span className="destination">{pkg.destination}, {pkg.country}</span>
                                        <span 
                                            className="category"
                                            style={{ backgroundColor: getCategoryColor(pkg.category) }}
                                        >
                                            {pkg.category.charAt(0).toUpperCase() + pkg.category.slice(1)}
                                        </span>
                                    </div>
                                    <h3 className="package-name">{pkg.name}</h3>
                                    <p className="package-desc">{pkg.description}</p>
                                    <div className="package-highlights">
                                        {pkg.highlights.slice(0, 3).map((highlight, idx) => (
                                            <span key={idx} className="highlight">{highlight}</span>
                                        ))}
                                    </div>
                                    <div className="package-info">
                                        <div className="info-item">
                                            <span className="label">{t('packages.duration', 'Duration')}:</span>
                                            <span className="value">{pkg.duration}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="label">{t('packages.groupSize', 'Group Size')}:</span>
                                            <span className="value">{pkg.groupSize}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="label">{t('packages.difficulty', 'Difficulty')}:</span>
                                            <span 
                                                className="value difficulty"
                                                style={{ color: getDifficultyColor(pkg.difficulty) }}
                                            >
                                                {pkg.difficulty.charAt(0).toUpperCase() + pkg.difficulty.slice(1)}
                                            </span>
                                        </div>
                                        <div className="info-item">
                                            <span className="label">{t('packages.price', 'Price')}:</span>
                                            <span className="value price">
                                                {pkg.originalPrice && (
                                                    <span className="original-price">{formatPrice(pkg.originalPrice)}</span>
                                                )}
                                                {formatPrice(pkg.price)}
                                            </span>
                                        </div>
                                    </div>
                                    <button className="btn btn-primary explore-btn">
                                        {t('packages.explore', 'Explore Package')}
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.section>

                {/* Filters */}
                <motion.section 
                    className="packages-filters"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                >
                    <div className="filters-header">
                        <h3>{t('packages.filters', 'Filters')}</h3>
                        <button onClick={clearFilters} className="clear-filters-btn">
                            {t('packages.clearFilters', 'Clear All')}
                        </button>
                    </div>

                    <div className="filters-grid">
                        <div className="filter-group">
                            <label>{t('packages.search', 'Search')}</label>
                            <input
                                type="text"
                                placeholder={t('packages.searchPlaceholder', 'Package, destination...')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="filter-input"
                            />
                        </div>

                        <div className="filter-group">
                            <label>{t('packages.category', 'Category')}</label>
                            <select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                className="filter-select"
                            >
                                {categories.map(category => (
                                    <option key={category} value={category}>
                                        {category === 'All' ? t('packages.allCategories', 'All Categories') : category}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>{t('packages.duration', 'Duration')}</label>
                            <select
                                value={selectedDuration}
                                onChange={(e) => setSelectedDuration(e.target.value)}
                                className="filter-select"
                            >
                                {durations.map(duration => (
                                    <option key={duration} value={duration}>
                                        {duration === 'All' ? t('packages.allDurations', 'All Durations') : duration}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>{t('packages.priceRange', 'Price Range')}</label>
                            <div className="price-range">
                                <input
                                    type="number"
                                    placeholder="Min"
                                    value={priceRange[0]}
                                    onChange={(e) => setPriceRange([parseInt(e.target.value) || 0, priceRange[1]])}
                                    className="filter-input"
                                />
                                <span>-</span>
                                <input
                                    type="number"
                                    placeholder="Max"
                                    value={priceRange[1]}
                                    onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value) || 10000])}
                                    className="filter-input"
                                />
                            </div>
                        </div>

                        <div className="filter-group">
                            <label>{t('packages.minRating', 'Minimum Rating')}</label>
                            <div className="rating-filter">
                                {[1, 2, 3, 4, 5].map(rating => (
                                    <button
                                        key={rating}
                                        className={`rating-btn ${selectedRating === rating ? 'active' : ''}`}
                                        onClick={() => setSelectedRating(selectedRating === rating ? 0 : rating)}
                                    >
                                        {'⭐'.repeat(rating)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="filter-group">
                            <label>{t('packages.sortBy', 'Sort By')}</label>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="filter-select"
                            >
                                <option value="name">{t('packages.sortName', 'Name')}</option>
                                <option value="price">{t('packages.sortPrice', 'Price')}</option>
                                <option value="rating">{t('packages.sortRating', 'Rating')}</option>
                                <option value="duration">{t('packages.sortDuration', 'Duration')}</option>
                            </select>
                        </div>
                    </div>
                </motion.section>

                {/* All Packages */}
                <motion.section 
                    className="all-packages"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                >
                    <div className="section-header">
                        <h2>
                            {t('packages.allPackages', 'All Packages')} ({filteredPackages.length})
                        </h2>
                        <p>
                            {filteredPackages.length > 0 
                                ? t('packages.packagesFound', 'packages found')
                                : t('packages.noPackages', 'No packages found matching your criteria')
                            }
                        </p>
                    </div>

                    {filteredPackages.length === 0 ? (
                        <div className="no-results">
                            <div className="no-results-icon">🎒</div>
                            <h4>{t('packages.noResultsTitle', 'No Packages Found')}</h4>
                            <p>{t('packages.noResultsDesc', 'Try adjusting your filters or search criteria')}</p>
                            <button onClick={clearFilters} className="btn btn-primary">
                                {t('packages.clearFilters', 'Clear Filters')}
                            </button>
                        </div>
                    ) : (
                        <div className="packages-grid">
                            {filteredPackages.map((pkg, index) => (
                                <motion.div
                                    key={pkg.id}
                                    className="package-card"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: index * 0.1 }}
                                    whileHover={{ y: -3, scale: 1.01 }}
                                >
                                    <div className="card-image">
                                        <img src={pkg.imageUrl} alt={pkg.name} />
                                        {pkg.isPopular && (
                                            <div className="popular-badge">🔥 Popular</div>
                                        )}
                                        {pkg.discount && (
                                            <div className="discount-badge">-{pkg.discount}%</div>
                                        )}
                                        <div className="rating-badge">
                                            {'⭐'.repeat(pkg.rating)}
                                        </div>
                                    </div>
                                    <div className="card-content">
                                        <div className="package-meta">
                                            <span className="destination">{pkg.destination}, {pkg.country}</span>
                                            <span 
                                                className="category"
                                                style={{ backgroundColor: getCategoryColor(pkg.category) }}
                                            >
                                                {pkg.category.charAt(0).toUpperCase() + pkg.category.slice(1)}
                                            </span>
                                        </div>
                                        <h3 className="package-name">{pkg.name}</h3>
                                        <p className="package-desc">{pkg.description}</p>
                                        <div className="package-category">
                                            <span 
                                                className="difficulty-tag"
                                                style={{ backgroundColor: getDifficultyColor(pkg.difficulty) }}
                                            >
                                                {pkg.difficulty.charAt(0).toUpperCase() + pkg.difficulty.slice(1)}
                                            </span>
                                        </div>
                                        <div className="package-info">
                                            <div className="info-item">
                                                <span className="label">{t('packages.duration', 'Duration')}:</span>
                                                <span className="value">{pkg.duration}</span>
                                            </div>
                                            <div className="info-item">
                                                <span className="label">{t('packages.groupSize', 'Group Size')}:</span>
                                                <span className="value">{pkg.groupSize}</span>
                                            </div>
                                            <div className="info-item">
                                                <span className="label">{t('packages.price', 'Price')}:</span>
                                                <span className="value price">
                                                    {pkg.originalPrice && (
                                                        <span className="original-price">{formatPrice(pkg.originalPrice)}</span>
                                                    )}
                                                    {formatPrice(pkg.price)}
                                                </span>
                                            </div>
                                        </div>
                                        <button className="btn btn-primary explore-btn">
                                            {t('packages.explore', 'Explore Package')}
                                        </button>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </motion.section>
            </div>
        </div>
    );
};

export default Packages;

